import { KeyRound, Hash, Repeat, Car, Wifi, ShieldOff, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const SERVICES = [{
  Icon: KeyRound,
  tag: "Most common",
  title: "Lost or broken remote",
  desc: "We bring brand-matched replacement remotes (B&D, Merlin, Chamberlain, ATA, Steel-Line and more), program on-site, and test from the driveway. Walk away with a working remote in under 30 minutes.",
  pill: "Same visit"
}, {
  Icon: Hash,
  tag: "Wall-mounted",
  title: "Keypad supply & install",
  desc: "Wireless number-pad on the outside wall — perfect for kids, cleaners, dog-walkers or when you've gone for a run and left the keys at home. Custom PIN, change it any time you like.",
  pill: "60 min install"
}, {
  Icon: Repeat,
  tag: "When brand-match is gone",
  title: "Universal remote programming",
  desc: "Original remote discontinued? We carry universal units (e.g. ATA TrioCodeGDO, Liftmaster universal) that pair with most legacy motors — even doors 15+ years old.",
  pill: "Legacy motors OK"
}, {
  Icon: Car,
  tag: "Built-in vehicle button",
  title: "Car HomeLink / Visor sync",
  desc: "Brand-new car with HomeLink (BMW, Audi, Tesla, Mercedes, many Toyotas)? We pair your car's built-in button to your garage motor — no extra remote needed on the visor.",
  pill: "All HomeLink cars"
}, {
  Icon: Wifi,
  tag: "Smart home",
  title: "Wi-Fi smart opener setup",
  desc: "Open and close from your phone, get alerts when the door's left open, share access with family. We install + commission MyQ, Merlin MyQ, B&D Smart Phone Control and most major hubs.",
  pill: "App-controlled"
}, {
  Icon: ShieldOff,
  tag: "Security & moving in",
  title: "Erase old / unknown remotes",
  desc: "Just bought the house? Tradies kept a copy? We factory-wipe the motor's remote memory, then re-pair only the remotes you actually want active. Peace of mind in 10 minutes.",
  pill: "Just moved in?"
}];
export default function RemoteServices() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Radial gradient ambient */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 20% 0%, ${NAVY_LIGHT}55 0%, ${NAVY} 50%), radial-gradient(circle at 85% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark numeral */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        03
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              03 · Every Remote Job, Covered
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            From a single lost remote to a full
            <br />
            smart-home setup — we do{" "}
            <span style={{
            color: GOLD
          }}>all of it</span>.
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            One callout, every remote / keypad / app issue handled. Tap the one
            that sounds like you — or just call and tell us what's happening.
          </p>
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map(({
          Icon,
          tag,
          title,
          desc,
          pill
        }, i) => <article key={title} className="relative rounded-2xl overflow-hidden transition-all hover:translate-y-[-2px] hover:shadow-2xl" style={{
          backgroundColor: `${NAVY_LIGHT}55`,
          border: `1px solid rgba(255,255,255,0.08)`,
          backdropFilter: "blur(6px)"
        }}>
              {/* Gold corner accent */}
              <div aria-hidden className="absolute top-0 left-0 h-px" style={{
            width: "60px",
            background: `linear-gradient(90deg, ${GOLD} 0%, transparent 100%)`
          }} />
              <div aria-hidden className="absolute top-0 left-0 w-px" style={{
            height: "60px",
            background: `linear-gradient(180deg, ${GOLD} 0%, transparent 100%)`
          }} />

              <div className="p-7">
                {/* Header row */}
                <div className="flex items-start justify-between gap-3 mb-6">
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg" style={{
                width: 52,
                height: 52,
                backgroundColor: `${GOLD}15`,
                border: `1px solid ${GOLD}33`
              }}>
                    <Icon size={24} color={GOLD} strokeWidth={2.25} />
                  </span>

                  <span style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: GOLD,
                padding: "5px 10px",
                borderRadius: "999px",
                backgroundColor: `${GOLD}12`,
                border: `1px solid ${GOLD}33`,
                whiteSpace: "nowrap"
              }}>
                    {pill}
                  </span>
                </div>

                {/* Tag */}
                <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(245,166,35,0.7)",
              marginBottom: "8px"
            }}>
                  {tag}
                </div>

                {/* Title */}
                <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.3rem",
              lineHeight: 1.2,
              color: "#ffffff",
              marginBottom: "12px",
              letterSpacing: "-0.015em"
            }}>
                  {title}
                </h3>

                {/* Desc */}
                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "14.5px",
              lineHeight: 1.65,
              color: "rgba(255,255,255,0.7)"
            }}>
                  {desc}
                </p>

                {/* Number stamp */}
                <div aria-hidden className="absolute bottom-4 right-5 pointer-events-none select-none" style={{
              fontFamily: FONT_HEAD,
              fontSize: "44px",
              fontWeight: 800,
              color: `${GOLD}10`,
              lineHeight: 1
            }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
            </article>)}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-12 rounded-2xl p-7 md:p-9 relative overflow-hidden" style={{
        background: `linear-gradient(135deg, ${NAVY_LIGHT}AA 0%, ${NAVY_DEEP} 100%)`,
        border: `1px solid ${GOLD}33`
      }}>
          {/* Decorative dots */}
          <div aria-hidden className="absolute top-0 right-0 w-40 h-40 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle, ${GOLD}33 1px, transparent 1.5px)`,
          backgroundSize: "12px 12px",
          opacity: 0.5,
          maskImage: "radial-gradient(circle at top right, black 0%, transparent 70%)"
        }} />

          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: "6px"
            }}>
                Not sure which one?
              </div>
              <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)",
              lineHeight: 1.2,
              color: "#ffffff",
              letterSpacing: "-0.015em"
            }}>
                Tell us the brand of your motor — we'll tell you the fix.
              </h3>
            </div>

            <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
              <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02]" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "14px",
              boxShadow: `0 8px 24px ${GOLD}33`
            }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Phone size={16} strokeWidth={2.5} />
                </span>
                Call 0485 000 933
              </a>

              <Link href="#contact-us" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full border transition-all hover:bg-white/10" style={{
              borderColor: "rgba(255,255,255,0.25)",
              color: "#ffffff",
              fontFamily: FONT_HEAD,
              fontWeight: 600,
              fontSize: "14px"
            }}>
                Get free quote
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ArrowRight size={14} strokeWidth={2.5} />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}